import { ReactNode, useEffect } from 'react'
import type { ResolvedAuthSession } from '@features/auth'
import { getAuthSession } from '@entities/auth'
import {
  applyAuthSession,
  clearAuthState,
  resolveAuthSession,
  useAuthStore,
} from '@features/auth'
import { Spinner } from '@shared/ui'
import { getWebSocketAuthToken } from '@shared/utils'

const SESSION_RECHECK_MIN_HIDDEN_MS = 30 * 1000

const getCurrentAuthSession = (): ResolvedAuthSession => {
  const { userId, svrId, brokerId } = useAuthStore.getState()

  return {
    userId,
    svrId,
    brokerId,
    accessToken: getWebSocketAuthToken(),
  }
}

const isSameSession = (current: ResolvedAuthSession, next: ResolvedAuthSession | null) => {
  if (!next) return false

  return (
    current.userId === next.userId &&
    current.svrId === next.svrId &&
    current.brokerId === next.brokerId &&
    current.accessToken === next.accessToken
  )
}

const isUnauthorizedSessionError = (error: unknown) => {
  const response = (error as { response?: { status?: number } }).response
  return response?.status === 401
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const isReady = useAuthStore(s => s.isReady)
  const isLoggedIn = useAuthStore(s => s.isLoggedIn)

  useEffect(() => {
    let canceled = false

    useAuthStore.getState().setReady(false)

    getAuthSession()
      .then(data => {
        if (canceled) return

        const session = resolveAuthSession(data)
        if (session) {
          applyAuthSession(session)
        } else {
          clearAuthState()
        }
      })
      .catch(() => {
        if (!canceled) clearAuthState()
      })
      .finally(() => {
        if (!canceled) useAuthStore.getState().setReady(true)
      })

    return () => {
      canceled = true
    }
  }, [])

  useEffect(() => {
    if (!isReady || !isLoggedIn) return

    let canceled = false
    let checking = false
    let hiddenAt = document.visibilityState === 'hidden' ? Date.now() : 0

    const reloadIfSessionChanged = () => {
      if (checking) return

      checking = true
      const currentSession = getCurrentAuthSession()

      getAuthSession()
        .then(data => {
          if (canceled) return

          const nextSession = resolveAuthSession(data)
          if (!isSameSession(currentSession, nextSession)) {
            window.location.reload()
          }
        })
        .catch(error => {
          if (!canceled && isUnauthorizedSessionError(error)) {
            window.location.reload()
          }
        })
        .finally(() => {
          checking = false
        })
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now()
        return
      }

      if (!hiddenAt) return

      const hiddenDuration = Date.now() - hiddenAt
      hiddenAt = 0

      if (hiddenDuration >= SESSION_RECHECK_MIN_HIDDEN_MS) {
        reloadIfSessionChanged()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      canceled = true
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isReady, isLoggedIn])

  if (!isReady) return <Spinner size="lg" fullscreen />

  return <>{children}</>
}
